import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { SAME_DAY, SAME_LOCATION } from "@/config/app-constant";

import { useTravelDatesContext } from "../../providers/travel-dates.provider";
import {
  checkCurentLocation,
  dateDifferenceInDays,
  isSameDay,
  stripTimeFromDate,
  returnCalculateDate,
} from "../../utils/date-picker.utils";
import {
  endDateValidationST,
  startDateValidationST,
} from "../../utils/date-validations.utils";

export function useDateSelectionST() {
  const context = useTravelDatesContext();
  const { current } = useWizard();
  const {
    maxFromDate,
    setCancelBtnClick,
    setIsDateError,
    setIsModalOpen,
    setModalType,
    setTravelDates,
    setTravelDateErrorMessage,
    setError,
    groupedMagnoliaData,
    keyForGroupedMagnolia,
    fromLocation,
    toLocation,
  } = context;

  const { errorMessage } = groupedMagnoliaData?.[keyForGroupedMagnolia] ?? {};

  const handleDateSelectionST = (
    date: Date[],
    skipSameDayValidation: boolean = false
  ) => {
    setModalType(null);
    setCancelBtnClick(false);
    setIsDateError(false);

    const [startDate, endDate] = date;
    const currentDate = stripTimeFromDate(new Date());

    if (!skipSameDayValidation) {
      setTravelDates({
        validate: false,
        startDate: startDate ?? null,
        endDate: endDate ?? null,
      });

      // Calculate maxDateForReturnST dynamically based on the NEW start date
      const baseDate = startDate ? startDate : new Date();
      const dynamicMaxDateForReturnST = returnCalculateDate(
        baseDate,
        null,
        null,
        current.brandSpecificParams?.maxDateRange as number
      );

      // Validate start date
      if (
        !startDateValidationST({
          startDate,
          maxFromDate,
          maxDateForReturnST: dynamicMaxDateForReturnST,
          errorMessage,
          setError,
        })
      ) {
        return;
      }
      // Validate end date
      if (
        !endDateValidationST({
          startDate,
          endDate,
          maxDateForReturnST: dynamicMaxDateForReturnST,
          errorMessage,
          setError,
          setTravelDates,
        })
      ) {
        return;
      }

      // Start day & same day modal
      if (isSameDay(startDate, currentDate)) {
        setTravelDates({
          validate: false,
          startDate,
          endDate,
        });
        setModalType(SAME_DAY);
        setIsModalOpen(true);
        return;
      }
    }

    // Same location modal (less than 2 days)
    if (
      checkCurentLocation(fromLocation, toLocation) &&
      dateDifferenceInDays(startDate, endDate) < 2
    ) {
      setTravelDates({
        validate: false,
        startDate,
        endDate,
      });
      setModalType(SAME_LOCATION);
      setIsModalOpen(true);
      return;
    }

    // Valid selection
    setIsDateError(false);
    setTravelDateErrorMessage("");
    setTravelDates({
      validate: true,
      startDate,
      endDate,
    });
  };

  const handleDateTypingST = (date: Date[]) => {
    setModalType(null);
    setCancelBtnClick(false);
    setIsDateError(false);

    const currentDate = new Date();
    const [startDate, endDate] = date;

    setTravelDates((prev) => ({
      ...prev,
      startDate: startDate?.toString() !== "Invalid Date" ? startDate : null,
      endDate: endDate?.toString() !== "Invalid Date" ? endDate : null,
    }));

    // Calculate maxDateForReturnST dynamically based on the NEW start date
    const baseDate = startDate ? startDate : new Date();
    const dynamicMaxDateForReturnST = returnCalculateDate(
      baseDate,
      null,
      null,
      current.brandSpecificParams?.maxDateRange as number
    );

    // Validate start date
    if (
      !startDateValidationST({
        startDate,
        maxFromDate,
        maxDateForReturnST: dynamicMaxDateForReturnST,
        errorMessage,
        setError,
      })
    ) {
      return;
    }
    // Validate end date
    if (
      !endDateValidationST({
        startDate,
        endDate,
        maxDateForReturnST: dynamicMaxDateForReturnST,
        errorMessage,
        setError,
        setTravelDates,
      })
    ) {
      return;
    }

    if (isSameDay(startDate, currentDate)) {
      setTravelDates({
        validate: false,
        startDate,
        endDate,
      });
      setModalType(SAME_DAY);
      setIsModalOpen(true);
      return;
    }
    if (
      checkCurentLocation(fromLocation ?? "", toLocation ?? "") &&
      dateDifferenceInDays(startDate, endDate) < 2
    ) {
      setModalType(SAME_LOCATION);
      setIsModalOpen(true);
      setTravelDates({
        validate: false,
        startDate,
        endDate,
      });
      return;
    }

    // Valid selection
    setIsDateError(false);
    setTravelDateErrorMessage("");
    setTravelDates({
      validate: true,
      startDate,
      endDate,
    });
  };

  return { handleDateSelectionST, handleDateTypingST };
}
